// Définition des services pour les pages SEO locales par prestation × ville.
// Chaque service a son propre URL prefix, hero, points forts, forfaits.

import { Camera, Sparkles, Building2 } from "lucide-react";

export const SERVICES = {
  photobooth: {
    slug: "photobooth",
    label: "Photobooth",
    icon: Camera,
    title_prefix: "Photobooth à",
    url_prefix: "photobooth",
    hero_image: "https://images.pexels.com/photos/787961/pexels-photo-787961.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    eyebrow_template: ({ city }) => `Photobooth premium · ${city.dept}`,
    subtitle_template: ({ city }) =>
      `Photobooth professionnel à ${city.name} : impressions illimitées, accessoires, fond personnalisé, livre d'or numérique. Idéal mariages, entreprises, anniversaires.`,
    intro_template: ({ city }) =>
      `À ${city.name}, le photobooth s'est imposé comme l'animation incontournable des mariages, séminaires et soirées familiales. Chez My Animation, notre setup combine appareil reflex pro, écran tactile, impressions instantanées et accessoires renouvelés chaque saison — pour des photos vraiment réussies.`,
    features: [
      "Appareil photo reflex professionnel + flash studio",
      "Impressions illimitées au format carte postale (10×15 cm)",
      "Fond personnalisable (logo entreprise, prénom des mariés…)",
      "Accessoires renouvelés tous les mois (chapeaux, perruques, panneaux)",
      "Livre d'or numérique : chaque invité laisse un mot avec sa photo",
      "Galerie en ligne privée envoyée le lendemain",
    ],
    packages: [
      { name: "Photobooth 3h", price: "dès 350 €", desc: "Setup complet + 1 animateur + impressions illimitées + livre d'or numérique." },
      { name: "Photobooth 5h", price: "dès 490 €", desc: "+ GIFs animés + fond logo entreprise/mariés.", featured: true },
      { name: "Photobooth Premium", price: "dès 690 €", desc: "+ Mur d'écran live + GIFs + customisation totale + 2e animateur." },
    ],
    keyword_prefix: "Photobooth",
    json_ld_service_type: ["Photobooth", "Animation événementielle", "Impressions photo"],
    testimonial_topic: "photobooth",
  },
  anniversaire: {
    slug: "anniversaire",
    label: "DJ Anniversaire",
    icon: Sparkles,
    title_prefix: "DJ Anniversaire à",
    url_prefix: "dj-anniversaire",
    hero_image: "https://images.pexels.com/photos/1813466/pexels-photo-1813466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    eyebrow_template: ({ city }) => `Anniversaire · ${city.dept}`,
    subtitle_template: ({ city }) =>
      `DJ anniversaire à ${city.name} pour vos 30, 40, 50, 60 ans : ambiance sur-mesure, playlist adaptée aux générations, animations rétro ou modernes. Forfaits dès 490 €.`,
    intro_template: ({ city }) =>
      `À ${city.name}, fêter un anniversaire marquant (30, 40, 50, 60 ans) demande un DJ qui sait jongler entre les générations : Cloclo pour les parents, Daft Punk pour les enfants, Booba pour les neveux. C'est exactement ce qu'on fait depuis plus de 10 ans dans la métropole lilloise.`,
    features: [
      "Playlist multi-générationnelle (années 60 à 2026)",
      "Sonorisation pro adaptée au lieu (salle privée, restaurant, à domicile)",
      "Micro HF pour les discours et surprises",
      "Jeux de lumière modulables (LED, lyres, machine à fumée)",
      "Possibilité d'ajouter karaoké ou blind test",
      "Préparation en 1-2 rendez-vous, gestion des dédicaces en temps réel",
    ],
    packages: [
      { name: "Essentiel", price: "dès 490 €", desc: "Sono pro + micro + 2 jeux de lumière, 4h de prestation." },
      { name: "Confort", price: "dès 690 €", desc: "+ Sub + lyres LED + uplighting basique, 6h. Idéal 30-50 invités.", featured: true },
      { name: "Premium", price: "dès 990 €", desc: "+ Machine à fumée + jeux dynamiques + DJ + blind test, 8h+." },
    ],
    keyword_prefix: "DJ Anniversaire",
    json_ld_service_type: ["DJ Anniversaire", "Animation musicale", "Sonorisation"],
    testimonial_topic: "anniversaire",
  },
  entreprise: {
    slug: "entreprise",
    label: "DJ Entreprise & Séminaire",
    icon: Building2,
    title_prefix: "DJ Entreprise / Séminaire à",
    url_prefix: "dj-entreprise",
    hero_image: "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    eyebrow_template: ({ city }) => `Événementiel B2B · ${city.dept}`,
    subtitle_template: ({ city }) =>
      `DJ professionnel pour vos soirées d'entreprise à ${city.name} : séminaire, soirée de fin d'année, lancement produit, gala. Devis sur-mesure sous 24h.`,
    intro_template: ({ city }) =>
      `À ${city.name} et dans toute la métropole lilloise, les entreprises font appel à My Animation pour leurs événements corporate : soirée fin d'année, séminaire interne, gala de remise de prix, lancement produit. Nous avons animé plus de 80 événements B2B en 2024-2025.`,
    features: [
      "Sonorisation professionnelle pour 30 à 500 personnes",
      "Régie son + lumière dédiée aux discours et keynotes",
      "Playlist corporate validée en amont avec votre équipe",
      "Animations sur-mesure : blind test corporate, karaoké interne, photobooth marqué",
      "Facturation entreprise (devis + facture HT/TTC, virement bancaire)",
      "Discrétion totale et tenue professionnelle exigée",
    ],
    packages: [
      { name: "Cocktail / Afterwork", price: "dès 590 €", desc: "Ambiance lounge + micro discours + jeux de lumière soft, 3-4h." },
      { name: "Soirée d'entreprise", price: "dès 990 €", desc: "Setup complet + DJ + animations + sono discours + lumières scéniques, 6h+.", featured: true },
      { name: "Gala / Séminaire premium", price: "dès 1 490 €", desc: "+ Double système + régie son keynote + photobooth corporate + uplighting." },
    ],
    keyword_prefix: "DJ Entreprise",
    json_ld_service_type: ["DJ Entreprise", "Animation événementielle", "Soirée corporate", "Séminaire"],
    testimonial_topic: "événement d'entreprise",
  },
};

export const SERVICE_SLUGS = Object.keys(SERVICES);
